import { NoopErrorTracker } from "./NoopErrorTracker";
import { PostHogErrorTracker } from "./PostHogErrorTracker";
import { getTelemetryConfig } from "./telemetryConfig";
import type { ErrorContext, ErrorTracker } from "./types";

let tracker: ErrorTracker = new NoopErrorTracker();
let initialized = false;

// Provider swap point: call this to replace the active tracker (e.g. with a
// SentryErrorTracker) instead of editing every call site in the app.
export function setErrorTracker(newTracker: ErrorTracker): void {
  tracker = newTracker;
}

// Idempotent — safe to call multiple times (e.g. under React StrictMode double-invocation).
export function initErrorTracking(): void {
  if (initialized) {
    return;
  }
  initialized = true;

  const { enabled } = getTelemetryConfig();
  tracker = enabled ? new PostHogErrorTracker() : new NoopErrorTracker();
  tracker.init();
}

// A single failed request can reach captureError from several layers: the axios response
// interceptor, then React Query's cache onError, then possibly window.unhandledrejection. Tracking
// the error objects already reported keeps that one PostHog event instead of three. A WeakSet
// means a reported error holds no memory once the app drops it.
const reportedErrors = new WeakSet<object>();

function alreadyReported(error: unknown): boolean {
  if (typeof error !== "object" || error === null) {
    // Primitives (thrown strings) have no identity to track — always report them.
    return false;
  }

  if (reportedErrors.has(error)) {
    return true;
  }

  reportedErrors.add(error);
  return false;
}

export function captureError(error: unknown, context?: ErrorContext): void {
  if (alreadyReported(error)) {
    return;
  }

  tracker.captureError(error, context);
}

export function identifyUser(userId: string, traits?: ErrorContext): void {
  tracker.identify(userId, traits);
}

export function resetUser(): void {
  tracker.reset();
}
